import { Ref, ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useProductStore } from '@/store/ProductStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useSubscriptionStore } from '@/store/SubscriptionStore'
import { useLazyQuery } from '@vue/apollo-composable'
import { fetchPlan as fetchPlanQuery, listPlans } from '@/queries'
import reducePlanResult from '@/composables/reducePlanResult'
import { sentryCapture } from '@/utils/sentry'
import { PlanResult, ProductId, type Plan, type PlanCurrency } from '@/types'

export const usePlanStore = defineStore('PlanStore', () => {
  const checkoutStore = useCheckoutStore()
  const productStore = useProductStore()
  const subscriptionStore = useSubscriptionStore()
  const selectedPlan: Ref<Plan | undefined> = ref()
  const plans: Ref<Plan[]> = ref([])

  const { planSet } = storeToRefs(productStore)
  const { currency } = storeToRefs(checkoutStore)

  const reducePlansResult = (plans: PlanResult[]): Plan[] => {
    return plans.map(plan => reducePlanResult(plan))
  }

  /* Fetch one plan */
  const { onResult: onFetchPlanResult, loading: planLoading, onError: onFetchPlanError } = useLazyQuery(fetchPlanQuery)
  /* Fetch all plans */
  const {
    onResult: onFetchPlansResult,
    load: fetchPlans,
    loading: plansLoading,
    onError: onFetchPlansError
  } = useLazyQuery(
    listPlans,
    { state: 'active' },
    {
      errorPolicy: 'ignore',
      fetchPolicy: 'cache-first',
      context: {
        fetchOptions: {
          method: 'GET'
        }
      }
    }
  )

  const isLoading = computed(() => planLoading.value || plansLoading.value)

  const updatePlans = (plan: Plan) => {
    plans.value = [...plans.value, plan]
  }

  const setSelectedPlan = (plan: Plan) => {
    const { setCheckoutProperty } = checkoutStore
    selectedPlan.value = plan

    const { code: planCode, trialRequiresBillingInfo: paymentRequired } = plan

    // set the plan in checkout store
    setCheckoutProperty({
      planCode,
      paymentRequired
    })
  }

  const resetSelectedPlan = () => {
    selectedPlan.value = undefined
  }

  const handlePlanCode = (planCode: string) => {
    if (selectedPlanCode.value === planCode) {
      return
    }

    const plan = plans.value.find(({ code }) => code === planCode)

    if (plan) {
      setSelectedPlan(plan)
    }
  }

  onFetchPlanResult(result => {
    if (!result.data?.fetchPlan) {
      return
    }

    const {
      data: { fetchPlan: fetchPlanResult }
    } = result

    if (!fetchPlanResult || fetchPlanResult.error) {
      return
    }

    const plan = reducePlanResult(fetchPlanResult)

    updatePlans(plan)

    setSelectedPlan(plan)
  })

  onFetchPlansResult(result => {
    if (!result.data?.listPlans) {
      return
    }

    const {
      data: { listPlans }
    } = result

    const reducedPlans = reducePlansResult(listPlans.data)

    plans.value = reducedPlans
  })

  onFetchPlansError(err => sentryCapture(err))
  onFetchPlanError(err => sentryCapture(err))

  const currentlyOfferedPlans = computed(() =>
    plans.value.filter((plan: any) => {
      const { currentlyOffered } = plan

      return currentlyOffered
    })
  )

  // Plans that require payment and have a ProductId (not promotional/offer)
  const standardPlans = computed(() =>
    currentlyOfferedPlans.value.filter(plan => plan.trialRequiresBillingInfo && Object.values(ProductId).includes(plan.planProduct as ProductId))
  )

  const selectedPlanProduct = computed(() => selectedPlan.value?.planProduct)
  const selectedPlanCode = computed(() => selectedPlan.value?.code)
  const selectedPlanInterval = computed(() => selectedPlan.value?.interval)
  const selectedPlanIntervalAsNoun = computed(() => selectedPlan.value?.intervalAsNoun)
  const selectedPlanTrial = computed(() => {
    return {
      length: selectedPlan.value?.trialLength,
      unit: selectedPlan.value?.trialUnit
    }
  })
  const selectedPlanTrialLength = computed(() => selectedPlanTrial.value?.length)
  const selectedPlanTrialLengthDisplay = computed(() => `${selectedPlan.value?.trialLength} ${selectedPlan.value?.trialUnit}`)
  const selectedPlanTrialLengthPluralDisplay = computed(() => `${selectedPlan.value?.trialLength} ${selectedPlan.value?.trialUnit}s`)
  const selectedPlanLengthDisplay = computed(() => {
    const length = selectedPlan.value?.intervalLength

    if (!length) return ''

    return length === 1 ? '1 month' : `${length} months`
  })

  const selectedPlanCurrency = computed(() => selectedPlan.value?.planCurrencies.find((planCurrency: PlanCurrency) => planCurrency.currency === currency.value))

  const selectedPlanAutoRenew = computed(() => selectedPlan.value?.autoRenew)
  const setPlanForProduct = () => {
    const { mostRecentSubscriptionInterval } = storeToRefs(subscriptionStore)
    if (!selectedPlan.value) {
      const planInterval = mostRecentSubscriptionInterval.value || 'monthly'
      const matchedPlan = currentlyOfferedPlans.value.find(plan => plan.planSet === planSet.value && plan.interval === planInterval)
      if (matchedPlan) {
        setSelectedPlan(matchedPlan)
      }
    }
  }

  const paymentRequired = computed(() => selectedPlan.value?.trialRequiresBillingInfo)

  const planFromNfrPlanSet = computed(() => {
    const { nfrPlanSet } = storeToRefs(productStore)

    return plans.value.find((plan: any) => plan.planSet === nfrPlanSet.value)
  })

  const hasSetupFee = computed(() => (selectedPlanCurrency.value?.setupFee ?? 0) > 0)
  const hasUnitAmount = computed(() => (selectedPlanCurrency.value?.unitAmount ?? 0) > 0)

  const isPayOncePlan = computed(() => hasSetupFee.value && !hasUnitAmount.value)

  return {
    currentlyOfferedPlans,
    fetchPlans,
    handlePlanCode,
    hasSetupFee,
    hasUnitAmount,
    isLoading,
    isPayOncePlan,
    paymentRequired,
    planFromNfrPlanSet,
    plans,
    resetSelectedPlan,
    selectedPlan,
    selectedPlanAutoRenew,
    selectedPlanCode,
    selectedPlanCurrency,
    selectedPlanInterval,
    selectedPlanIntervalAsNoun,
    selectedPlanLengthDisplay,
    selectedPlanProduct,
    selectedPlanTrial,
    selectedPlanTrialLength,
    selectedPlanTrialLengthDisplay,
    selectedPlanTrialLengthPluralDisplay,
    setPlanForProduct,
    setSelectedPlan,
    standardPlans
  }
})
